// Initial Redux state for the Governance feature (Data Controls in this delivery).
// Mirrors the shape used by other entity states (customersState, etc.).
export const governanceInitialState = {
  // The control type currently being edited. Web/AI/Extension are stubs for now.
  controlType: 'DATA',

  // Published policy view: { policyId, version, rules: [] } (version may be null).
  policy: null,
  policyLoading: false,
  // Control type the current `policy`/`draft` belong to (DATA/WEB/AI share this one
  // slice). Consumers ignore data whose type ≠ the active type — see the landing's
  // guard and governance.js policyReceived/draftReceived.
  policyType: null,
  draftType: null,

  // The open draft view: { policyId, version, rules: [] } (version null = no draft).
  draft: null,
  draftLoading: false,

  // WEB/AI "policy = site" landing: one entry per site policy, each with its own
  // published/open-draft version state + primary app metadata. See listGovernanceSites.
  sites: [],
  sitesType: null,
  sitesLoading: false,

  // WEB Unsanctioned Apps discovery list (read-only; PIXMC-6083 §9/§10) — the
  // domains the daily job found but no admin has sanctioned/denied yet. Own
  // records/filters, independent of the sites landing list above.
  unsancApps: [],
  unsancAppsTotalCount: 0,
  unsancAppsLookbackDays: null,
  unsancAppsLoading: false,
  unsancAppsFilters: {
    page: 1,
    pageSize: 10,
    search: '',
    sortBy: 'users',
    sortDir: 'desc',
  },

  // ----- Extension Controls -----
  // The Extension Policy block (PIXMC-6539): now PAGED, and it also lists discovered
  // (ungoverned) extensions, not just authored policies + the default catch-all. One
  // record per entry, carrying its effective (rule-aware) status and a source tag
  // (Admin = authored, System = default/discovered).
  extensionPolicies: [],
  extensionPolicyTotalCount: 0,
  extensionPoliciesLoading: false,
  // The top table's OWN filter/paging state — SEPARATE from extensionFilters, which
  // drives the Policy Evaluation block below. pageSize 10 matches the mockup.
  extensionPolicyFilters: {
    search: '',
    disposition: '',
    page: 1,
    pageSize: 10,
  },
  // The three stat tiles, from a SEPARATE endpoint (/governance/extension/counts) so
  // they are decoupled from the table's pagination/search/filter and can never move
  // when the table is filtered. Rule-aware totals of the whole extension universe.
  extensionCounts: { denied: 0, sanctioned: 0, unsanctioned: 0 },
  extensionCountsLoading: false,

  // The fixed catalogue a PixmAdmin may attach from. Read-only, seeded in the DB —
  // the portal cannot author or edit a rule definition.
  extensionPredefinedRules: [],
  extensionPredefinedRulesLoading: false,

  // The Policy Evaluation block is a CLIENT-SIDE JOIN of two independent reads,
  // because they live in different databases and cannot be joined in SQL:
  //   extensionLookup     — Configuration DB: names, publisher, disposition
  //   extensionEvaluation — Customer DB: unique DEVICE count, last seen, status
  // Matched on logicalExtensionId, within the customer already scoping both calls.
  extensionLookup: [],
  extensionLookupTotalCount: 0,
  extensionLookupLoading: false,

  //   extensionNames — logicalExtensionId -> extensionName, for the Incident Feed's Extension
  //     Violations tab. A violation row carries only the logical id (AppIdentifier); the name
  //     lives in the Configuration DB while the violations live in the Customer DB, so there is
  //     no SQL join and the two are matched client-side.
  //
  //     A SEPARATE slice from extensionLookup on purpose: that one is filtered by the Extension
  //     Controls screen's search/disposition and drives its stat-card counts, so sharing it would
  //     make one screen's fetch visibly corrupt the other's numbers.
  extensionNames: {},
  extensionEvaluation: [],
  extensionEvaluationLoading: false,
  // Status filter only. There is deliberately no "By user" grouping — it is not
  // being delivered, so the aggregate returns no user count to group by.
  extensionFilters: {
    search: '',
    disposition: '',
  },
  attachingExtensionRule: false,
  // The SPECIFIC server error from an attach/edit of an extension rule, kept apart
  // from the shared `message` so the editor can show what actually went wrong (e.g.
  // ERR_GOV_019's "an extension policy for this extension already exists") instead of
  // a generic failure line. Written only by the extension-rule reducers, read only by
  // the extension editor, so a stale value cannot bleed into another screen.
  extensionRuleErrorCode: null,
  extensionRuleErrorDesc: null,

  // CRUD / draft / publish UI flags.
  savingRule: false,
  creatingDraft: false,
  publishing: false,

  // Specific "create site" failure (PIXMC-6083 §10.4) — deliberately its OWN pair of
  // fields, not the shared statusCode/message/error above. Those are written by many
  // unrelated actions (policy/draft/sites fetches), so a component reacting to them
  // on mount risks popping an error left over from a different request. createSite is
  // the only writer of these two, and SiteCreatorBase clears them on mount, so a stale
  // value from a previous attempt can never leak into an unrelated screen.
  createSiteErrorCode: null,
  createSiteErrorDesc: null,

  // Default rules template (read from MST_SCAN_RULE; shown on brand-new policy, never written to DB).
  defaultRules: [],
  defaultRulesLoading: false,

  // Usage counts keyed by ruleId (from GovernanceRuleHitDaily).
  usage: {},
  usageLoading: false,

  // Generic status/message surface (matches the ConfirmPopup pattern used elsewhere).
  statusCode: null,
  message: '',
  error: false,
}
